// src/components/UiFeedbackWidget/UiFeedbackPanel.jsx
//
// Spec 20260925-ui-feedback §2 / §6.1 / §6.4, plan T1.27. Tasks T049.
//
// A NON-MODAL panel (AC-2): a fixed Paper, no Dialog, no backdrop, no focus trap —
// role="dialog" with aria-modal="false". The page underneath stays scrollable and
// clickable, which is the point: the user is looking at what they are describing.
// 360px on sm+, a half-height bottom sheet on xs.
//
// The fastest path is button → type → Enter (AC-3). Enter sends, Shift+Enter is a new
// line, Esc closes (the picker's own Esc handler runs first while pointing). The send
// button stays disabled below 10 characters; the counter appears only after a try.
//
// Presentational: all state lives in ./index.jsx.

import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  PushPinOutlined as PinIcon,
  AdsClick as PickIcon,
  Send as SendIcon,
  PlaceOutlined as ElementIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UI_FEEDBACK_CATEGORIES, UI_FEEDBACK_MIN_BODY, scanForPii } from '@caro/shared/ideas';

import { displayNameFor } from './elementIdentity';

const TOUCH = { minHeight: 48 };

export default function UiFeedbackPanel({
  page,
  pinned,
  onUnpin,
  body,
  onBodyChange,
  categories,
  onToggleCategory,
  element,
  onClearElement,
  onStartPick,
  picking,
  onSubmit,
  onClose,
  busy,
  attempted,
  error,
  success,
  piiNotice,
  canViewIdeas,
  onOpenIdeabox,
  inputRef,
  bottom,
}) {
  const { t } = useTranslation();
  const length = body.trim().length;
  const noRoute = !page || !page.route;
  const canSend = !busy && !noRoute && length >= UI_FEEDBACK_MIN_BODY;
  // Phase 2 (V-8a): an ADVISORY scan while typing, only shown on a hit, never blocking. The
  // server scans again on submit and the triage PII gate stays the control.
  const piiHit = useMemo(() => body.trim().length > 0 && scanForPii(null, body).length > 0, [body]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      onSubmit();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  const errorText = (() => {
    if (!error) return null;
    if (error.kind === 'rate_limited') {
      return error.limit
        ? t('Ideas.uiFeedback.error_rate_limited_n', { count: error.limit })
        : t('Ideas.uiFeedback.error_rate_limited');
    }
    if (error.kind === 'forbidden') return t('Ideas.uiFeedback.error_forbidden');
    return t('Ideas.uiFeedback.error_generic');
  })();

  return (
    <Paper
      data-ui-feedback-root
      data-testid="ui-feedback-panel"
      role="dialog"
      aria-modal="false"
      aria-labelledby="ui-feedback-title"
      elevation={8}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !picking) onClose();
      }}
      sx={{
        position: 'fixed',
        zIndex: (theme) => theme.zIndex.appBar + 1,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        p: 2,
        // xs: a half-height bottom sheet over the MobileBottomNav.
        left: { xs: 0, sm: 'auto' },
        right: { xs: 0, sm: 16 },
        // Above the FAB on sm+ (the container passes the FAB's own bottom + its height).
        bottom: { xs: 0, sm: (bottom && bottom.sm) || 88, md: (bottom && bottom.md) || 88 },
        width: { xs: '100%', sm: 360 },
        height: { xs: '50vh', sm: 'auto' },
        maxHeight: { sm: 'calc(100vh - 120px)' },
        borderRadius: { xs: '12px 12px 0 0', sm: 2 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography id="ui-feedback-title" variant="subtitle1" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
          {t('Ideas.uiFeedback.title')}
          {page && page.pageLabel ? ` · ${page.pageLabel}` : ''}
          {pinned ? ` ${t('Ideas.uiFeedback.pinned')}` : ''}
        </Typography>
        {pinned ? (
          <Tooltip title={t('Ideas.uiFeedback.unpin')}>
            <IconButton
              aria-label={t('Ideas.uiFeedback.unpin')}
              onClick={onUnpin}
              data-testid="ui-feedback-unpin"
              sx={{ width: 48, height: 48 }}
            >
              <PinIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <IconButton
          aria-label={t('Ideas.uiFeedback.close')}
          onClick={onClose}
          data-testid="ui-feedback-close"
          sx={{ width: 48, height: 48 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {success ? (
        <Alert severity="success" role="status" sx={{ mb: 1 }} data-testid="ui-feedback-success">
          {t('Ideas.uiFeedback.thanks')}{' '}
          {success.publicRef && canViewIdeas ? (
            <Link component={RouterLink} to={`/ideas/${success.publicRef}`}>
              {`IDEA-${success.publicRef}`}
            </Link>
          ) : success.publicRef ? (
            <strong>{`IDEA-${success.publicRef}`}</strong>
          ) : null}
        </Alert>
      ) : null}

      {piiNotice ? (
        <Alert severity="info" sx={{ mb: 1 }} data-testid="ui-feedback-pii-notice">
          {t('Ideas.uiFeedback.pii_after')}
        </Alert>
      ) : null}

      {errorText ? (
        <Alert severity="error" sx={{ mb: 1 }} data-testid="ui-feedback-error">
          {errorText}
        </Alert>
      ) : null}

      {noRoute ? (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="ui-feedback-no-route">
          {t('Ideas.uiFeedback.no_route')}
        </Alert>
      ) : null}

      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={3}
        maxRows={8}
        placeholder={t('Ideas.uiFeedback.placeholder')}
        inputProps={{ 'aria-label': t('Ideas.uiFeedback.placeholder'), maxLength: 5000 }}
        inputRef={inputRef}
        value={body}
        onChange={(e) => onBodyChange(e.target.value)}
        onKeyDown={onKeyDown}
        data-testid="ui-feedback-body"
      />
      {piiHit ? (
        <Alert severity="info" sx={{ mt: 1 }} data-testid="ui-feedback-pii-warning" role="status">
          {t('Ideas.uiFeedback.pii_before')}
        </Alert>
      ) : null}
      {attempted && length < UI_FEEDBACK_MIN_BODY ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5 }}
          data-testid="ui-feedback-counter"
          aria-live="polite"
        >
          {t('Ideas.uiFeedback.counter', { count: length, min: UI_FEEDBACK_MIN_BODY })}
        </Typography>
      ) : null}

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 1.5 }}>
        {UI_FEEDBACK_CATEGORIES.map((c) => {
          const on = categories.includes(c);
          return (
            <Chip
              key={c}
              label={t(`Ideas.uiFeedback.chip_${c}`)}
              color={on ? 'primary' : 'default'}
              variant={on ? 'filled' : 'outlined'}
              onClick={() => onToggleCategory(c)}
              aria-pressed={on ? 'true' : 'false'}
              data-testid={`ui-feedback-chip-${c}`}
              sx={{ height: { xs: 48, sm: 36 } }}
            />
          );
        })}
      </Stack>

      {element ? (
        <Box sx={{ mt: 1.5 }}>
          <Chip
            icon={<ElementIcon />}
            label={displayNameFor(element.elementKbId, element.elementRole, t)}
            onDelete={onClearElement}
            deleteIcon={<CloseIcon aria-label={t('Ideas.uiFeedback.clear_element')} />}
            data-testid="ui-feedback-element-chip"
            sx={{ height: { xs: 48, sm: 36 }, maxWidth: '100%' }}
          />
        </Box>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
        <Button
          variant="outlined"
          startIcon={<PickIcon />}
          onClick={onStartPick}
          disabled={picking}
          data-testid="ui-feedback-pick"
          sx={TOUCH}
        >
          {t('Ideas.uiFeedback.pick')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={onSubmit}
          disabled={!canSend}
          data-testid="ui-feedback-send"
          sx={TOUCH}
        >
          {t('Ideas.uiFeedback.send')}
        </Button>
      </Stack>

      {canViewIdeas ? (
        <Button
          variant="text"
          size="small"
          onClick={onOpenIdeabox}
          data-testid="ui-feedback-ideabox-link"
          sx={{ mt: 1, alignSelf: 'flex-start', ...TOUCH }}
        >
          {t('Ideas.uiFeedback.ideabox_link')}
        </Button>
      ) : null}
    </Paper>
  );
}
